<script setup lang="ts">
    import {
        computed,
        onBeforeUnmount,
        provide,
        ref,
        useTemplateRef,
        watch,
        defineAsyncComponent,
    } from 'vue'
    import { useI18n } from 'vue-i18n'
    import { useDropZone, useResizeObserver } from '@vueuse/core'
    import { storeToRefs } from 'pinia'
    import { generateId } from 'ai'
    import PkAuroraCanvas from './PkAuroraCanvas.vue'
    import PkAvatar from './PkAvatar.vue'
    import PkChatbotMessages from './PkChatbotMessages.vue'
    import PkChatbotInput from './PkChatbotInput.vue'
    import PkChatbotQueuedMessages from './PkChatbotQueuedMessages.vue'
    import PkChatbotEmptyState from './PkChatbotEmptyState.vue'
    import PkVoiceSession from './PkVoiceSession.vue'
    import PkChatbotWakeupBar from './PkChatbotWakeupBar.vue'
    import { useVoiceSession } from './useVoiceSession'
    import { useChatFilePreviewDock } from './chatFilePreviewDock'
    import { useWakeupWatch } from './useWakeupWatch'
    import { useChatFilePreview } from './useChatFilePreview'
    import {
        chatFileName,
        formAnswerAsSpoken,
        getPartState,
        HOST_STEP_TITLES,
        type ChatFilePreviewTarget,
        type ChatFileRestoreTarget,
        type WordPreviewMode,
    } from './utils'
    import { getFilePartMedia, isFilePart } from 'utils'
    import {
        useChatbotStore,
        useLocalizedString,
        useGreeting,
        VOICE_LINE_ID_PREFIX,
    } from 'composables'
    import { chatDropAccepts, type UIChatMessage } from 'models'

    const PkToolShowForm = defineAsyncComponent(
        () => import('./PkToolShowForm.vue'),
    )
    const PkToolShowContactForm = defineAsyncComponent(
        () => import('./PkToolShowContactForm.vue'),
    )
    const PkToolShowSuggestedReply = defineAsyncComponent(
        () => import('./PkToolShowSuggestedReply.vue'),
    )
    const PkToolShowSources = defineAsyncComponent(
        () => import('./PkToolShowSources.vue'),
    )
    const PkToolRequestGeolocation = defineAsyncComponent(
        () => import('./PkToolRequestGeolocation.vue'),
    )
    const PkToolRequestOAuthConnection = defineAsyncComponent(
        () => import('./PkToolRequestOAuthConnection.vue'),
    )
    const PkToolShowLocation = defineAsyncComponent(
        () => import('./PkToolShowLocation.vue'),
    )
    const PkToolShowDiagram = defineAsyncComponent(
        () => import('./PkToolShowDiagram.vue'),
    )
    const PkToolChatFile = defineAsyncComponent(
        () => import('./PkToolChatFile.vue'),
    )
    const PkToolShareDocument = defineAsyncComponent(
        () => import('./PkToolShareDocument.vue'),
    )
    const PkFilePreview = defineAsyncComponent(
        () => import('./PkFilePreview.vue'),
    )
    const PkFilePreviewDialog = defineAsyncComponent(
        () => import('./PkFilePreviewDialog.vue'),
    )

    const props = defineProps<{
        agentId: string
        /** Mirrors the PkChatbot `modifier` prop: `fullscreen` shows a centered empty state (greeting) instead of the welcome message, `panel` behaves like `widget` */
        modifier?: 'widget' | 'fullscreen' | 'panel'
        /** Display name of the current user, used for the `{name}` greeting placeholder */
        userName?: string
    }>()

    const emit = defineEmits<{
        'show-info': [message: UIChatMessage]
        revise: [message: UIChatMessage]
    }>()

    const store = useChatbotStore(props.agentId)

    const {
        agentInterface,
        agentFileUpload: storedFileUpload,
        agentType,
        agentVoice,
        actions,
        revisedAnswers,
        messages,
        chat,
        messageFeedbacks,
        feedbackDialogMessage,
        isFeedbackSubmitting,
        isFeedbackSubmitted,
        feedbackSubmitError,
        isLeadSubmitted,
        isLoadingSubmitLead,
        submitLeadError,
        input,
        inputMessagePlaceholder,
        isConversationBlocked,
        baseUrl,
        pendingAttachments,
        isAttachmentLimitReached,
        fileLimitError,
        apiClient,
        isDark,
        hostToolsPending,
        pageNoticeLevel,
        hostConfirmation,
        agentPageAccess,
        hostStepTitles,
        canQueue,
        queuedMessages,
        backgroundAnswers,
        inputFocusRequest,
        pagePointer,
        pagePointerAvailable,
        pagePointing,
    } = storeToRefs(store)
    // The steps of the page name what they act on while the page is open
    provide(HOST_STEP_TITLES, hostStepTitles)

    // The host asks for the message field (⇧Esc on the chat page)
    const inputEl =
        useTemplateRef<InstanceType<typeof PkChatbotInput>>('inputEl')
    watch(inputFocusRequest, () => {
        inputEl.value?.focus()
    })
    // What the user just pointed at on the page goes with what they write
    // next (spec page-point-and-choose.md, P): the field takes the focus
    watch(pagePointer, (pointer) => {
        if (pointer) {
            inputEl.value?.focus()
        }
    })
    const pointerChip = computed(() =>
        pagePointer.value
            ? { kind: pagePointer.value.kind, label: pagePointer.value.label }
            : undefined,
    )

    // A reasoning agent reads no attachment, and the backend refuses one: no
    // attach button and no drop zone, whatever its upload settings say.
    const agentFileUpload = computed(() =>
        agentType.value === 'reasoning' ? undefined : storedFileUpload.value,
    )

    /** A file the agent stored in this chat, such as a spreadsheet version. */
    const chatFileDownloadUrl = (storedFileName: string) =>
        apiClient.value.getMediaDownloadUrl(
            props.agentId,
            store.localChatId,
            storedFileName,
        )

    const {
        handleSubmit: storeHandleSubmit,
        stopGeneration,
        regenerate,
        onUpvote,
        onDownvote,
        onFeedback,
        onFeedbackSubmit,
        onLeadSubmit,
        startNewChat,
        addToolOutput,
        addToolApprovalResponse,
        handleFileSelect,
        clearFileLimitError,
        refreshMessages,
        appendMessage,
        closePageNotice,
        respondHostConfirmation,
        editQueuedMessage,
        removeQueuedMessage,
        openBackgroundAnswer,
        dismissBackgroundAnswer,
    } = store

    const { t: $t } = useI18n({ useScope: 'global' })

    // The embedding page runs calls of the last answer (spec `host-tools.md`):
    // the request that announced them is over and the chat reads `ready`, yet
    // the answer is not. The input shows the work and its stop.
    const hostWorking = computed(() => hostToolsPending.value > 0)
    const inputStatus = computed(() =>
        hostWorking.value && chat.value.status === 'ready'
            ? 'streaming'
            : chat.value.status,
    )
    // The customer's own words when the agent has them, else what the
    // level allows.
    const customPageNotice = useLocalizedString(
        () => agentPageAccess.value?.notice,
    )
    const pageNotice = computed(() =>
        pageNoticeLevel.value
            ? customPageNotice.value ||
              $t(`message.pageAccessNotice.${pageNoticeLevel.value}`)
            : undefined,
    )

    const attachmentNotice = computed(() => {
        const err = fileLimitError.value
        if (!err) {
            return undefined
        }
        return err.reason === 'per-chat'
            ? $t('message.fileLimitPerChat', { max: err.max })
            : $t('message.fileLimitPerMessage', { max: err.max })
    })

    // Answers that ended in a conversation the user left (spec
    // resumable-chat-streams, F2), named by its title when the list has it.
    const answerNotices = computed(() =>
        backgroundAnswers.value.map(({ chatId, title }) => ({
            id: chatId,
            text: title
                ? $t('message.backgroundAnswerReady', { title })
                : $t('message.backgroundAnswerReadyUntitled'),
        })),
    )

    const dismissableNotice = useLocalizedString(
        () => agentInterface.value?.dismissableNotice,
    )

    // Fullscreen empty state: shown until the user sends the first message.
    // The synthetic welcome message is suppressed by the store in fullscreen
    // (`hideWelcomeMessage`), the greeting takes its place.
    const hasUserMessages = computed(() =>
        messages.value.some((message) => message.role === 'user'),
    )
    const showEmptyState = computed(
        () => props.modifier === 'fullscreen' && !hasUserMessages.value,
    )
    const greeting = useGreeting(agentInterface, () => props.userName)

    const chatViewEl = useTemplateRef<HTMLDivElement>('chatViewEl')

    // #region file preview
    /**
     * Where the preview of a Word document or a spreadsheet opens (spec
     * `chat-office-files.md`, F6, items 3 and 4): beside the chat when the chat has room for a reading
     * column and the panel, over it in a dialog otherwise. Measured on the
     * chat, not on the window: the floating widget, the playground and a page
     * with the Copilot docked beside it are narrow on a wide screen.
     */
    const PREVIEW_PANEL_MIN_WIDTH = 1000
    /**
     * A page that lays the chat out between its own columns gives the panel
     * a column of the page's height (`provideChatFilePreviewDock`): the
     * panel takes its width from the chat, so the two together are the room
     * the panel opens in.
     */
    const previewDock = useChatFilePreviewDock()
    const previewDocked = computed(() => !!previewDock?.value)
    // A ref read in the template is unwrapped: the target goes through here
    const previewDockTarget = computed(() => previewDock?.value ?? 'body')
    const chatViewWidth = ref(0)
    const dockWidth = ref(0)
    // The border box: the panel's room is padding of the chat, and measured
    // without it the open panel took the width that opened it. One observer
    // for the chat and the dock: with two, the chat narrowed by the panel
    // arrived alone, the panel closed before the dock's width came, and the
    // preview went back and forth between the panel and the dialog.
    useResizeObserver(
        [chatViewEl, computed(() => previewDock?.value ?? undefined)],
        (entries) => {
            for (const entry of entries) {
                const width =
                    entry.borderBoxSize?.[0]?.inlineSize ??
                    entry.contentRect.width
                if (entry.target === chatViewEl.value) {
                    chatViewWidth.value = width
                } else {
                    dockWidth.value = width
                }
            }
        },
        { box: 'border-box' },
    )
    const previewInPanel = computed(
        () =>
            chatViewWidth.value + (previewDocked.value ? dockWidth.value : 0) >=
            PREVIEW_PANEL_MIN_WIDTH,
    )
    const previewFullscreen = computed(
        () => chatViewWidth.value > 0 && chatViewWidth.value < 576,
    )
    const {
        target: previewTarget,
        preview: previewData,
        loading: previewLoading,
        error: previewError,
        open: openFilePreview,
        close: closeFilePreview,
        reset: resetPreview,
    } = useChatFilePreview((storedFileName) =>
        apiClient.value.getChatFilePreview(
            props.agentId,
            store.localChatId,
            storedFileName,
        ),
    )
    /**
     * How the open file is shown: its revisions, sheet and zoom. The viewer
     * is drawn anew when it moves between the panel and the dialog, and
     * takes these along; another file starts afresh.
     */
    const previewView = ref<{
        mode?: WordPreviewMode
        sheet?: number
        zoom?: number
        fit?: boolean
    }>({})

    const openPreview = (target: ChatFilePreviewTarget) => {
        const fileOf = (t?: ChatFilePreviewTarget) =>
            t?.fileId ?? t?.storedFileName
        if (fileOf(target) !== fileOf(previewTarget.value)) {
            previewView.value = {}
        }
        return openFilePreview(target)
    }

    /** The panel grown into a dialog over the chat, on the viewer's button. */
    const previewExpanded = ref(false)
    const previewBeside = computed(
        () =>
            !!previewTarget.value &&
            previewInPanel.value &&
            !previewExpanded.value,
    )

    const closePreview = () => {
        closeFilePreview()
        previewExpanded.value = false
    }

    /**
     * Every version of each file in the conversation, by its fileId: the
     * file written by the user or the agent is version 1, its stored name is
     * the fileId.
     */
    const chatFileVersions = computed(() => {
        const byFile = new Map<string, Map<number, string>>()
        for (const message of messages.value) {
            for (const part of message.parts) {
                const { type, state, output } = part as {
                    type: string
                    state?: string
                    output?: {
                        fileId?: string
                        version?: number
                        storedFileName?: string
                    }
                }
                if (
                    CHAT_FILE_TOOL_PARTS.has(type) &&
                    state === 'output-available' &&
                    output?.fileId &&
                    output.version &&
                    output.storedFileName
                ) {
                    const versions = byFile.get(output.fileId) ?? new Map()
                    versions.set(output.version, output.storedFileName)
                    byFile.set(output.fileId, versions)
                }
            }
        }
        for (const [fileId, versions] of byFile) {
            if (!versions.has(1)) {
                versions.set(1, fileId)
            }
        }
        return byFile
    })
    const previewVersions = computed(() =>
        [
            ...(chatFileVersions.value
                .get(previewTarget.value?.fileId ?? '')
                ?.keys() ?? []),
        ].sort((a, b) => a - b),
    )
    const showPreviewVersion = (version: number) => {
        const target = previewTarget.value
        const storedFileName = chatFileVersions.value
            .get(target?.fileId ?? '')
            ?.get(version)
        if (target && storedFileName) {
            openPreview({ ...target, version, storedFileName })
        }
    }
    /** The shown version is an earlier one the agent can write again. */
    const previewRestorable = computed(() => {
        const target = previewTarget.value
        const versions = previewVersions.value
        return (
            !!chatFileRestore.value &&
            !!target?.fileId &&
            !!target.version &&
            versions.length > 0 &&
            target.version < versions[versions.length - 1]
        )
    })
    const restoreFromPreview = () => {
        const target = previewTarget.value
        if (target?.fileId && target.version) {
            chatFileRestore.value?.({
                fileId: target.fileId,
                version: target.version,
                filename: target.filename,
            })
            closePreview()
        }
    }

    // Another chat has other files.
    watch(() => store.localChatId, resetPreview)
    // #endregion

    const handleExpandSourceContext = async (payload: {
        documentId: string
        chunkIndex: number
    }) => {
        const result = await apiClient.value.expandSourceContext(
            props.agentId,
            payload.documentId,
            payload.chunkIndex,
        )
        return result.content
    }

    const handleDownloadSource = async (documentId: string) => {
        const result = await apiClient.value.downloadSourceDocument(
            props.agentId,
            documentId,
        )
        window.open(result.downloadUrl, '_blank')
    }

    const handleFileDrop = (files: File[] | null) => {
        if (!agentFileUpload.value?.enabled || !files) {
            return
        }
        for (const file of files) {
            handleFileSelect(file)
        }
    }

    const { isOverDropZone } = useDropZone(chatViewEl, {
        dataTypes: (types) =>
            chatDropAccepts(
                types,
                agentFileUpload.value?.allowedMimeTypes ?? [],
            ),
        onDrop: handleFileDrop,
    })

    // #region voice
    /**
     * A spoken turn is a normal turn: the provider calls our backend, which
     * persists it into this very conversation. So the client does not answer
     * anything here, it only shows the transcript as it arrives and then reloads
     * the conversation from the server, which is the version that also carries
     * sources and tool results. When the call ends the input comes back with the
     * whole exchange above it, ready to continue in writing.
     */
    const isMuted = ref(false)
    // The session closes asynchronously, so `onTurnEnd` can still fire after
    // this view is gone: without the flag it would schedule a reload of a
    // conversation nobody is showing.
    let isUnmounted = false

    /**
     * True between the caller finishing a sentence and the answer starting.
     *
     * On a call nothing of the turn passes through this client, so the feed has
     * no way of its own to show that something is happening: the question sits
     * there alone until the answer is written and reloaded, which on a
     * retrieving turn is a long ten seconds of a screen that looks stuck.
     */
    const isAwaitingSpokenAnswer = ref(false)

    /**
     * How the conversation catches up with what the server persisted: one
     * sequential chain of reloads, the next scheduled only when the previous
     * one has answered, so slow responses never pile up. It replaces an
     * interval plus a backed-off retry ladder that could overlap each other.
     *
     * While the call is live it looks every {@link POLL_LIVE_MS}. When the
     * call is over (hang-up, provider disconnect, time cap) it keeps looking:
     * the turn that killed the call is by definition longer than the
     * provider's own timeout, and its write can land minutes later. Measured
     * live (2026-08-26, chat FYYaVLwX1UyU3CnQ): answers persisted 172 seconds
     * and 6.5 minutes after their turn started, both past the fixed 60-second
     * grace this replaces, and the screen stayed frozen until a manual reload.
     */
    const POLL_LIVE_MS = 3000
    const POLL_ENDED_BACKOFF_MS = [3000, 5000, 10_000]
    /**
     * The after-call poll never gives up before this, even with nothing on
     * screen looking missing: a write can land late and leave no trace to wait
     * for, because a turn the provider never transcribed has no spoken line of
     * its own.
     */
    const POLL_ENDED_MIN_MS = 60_000
    /**
     * And this much when the call ended badly, which is the case where a turn
     * is certainly still running server-side: the provider hangs up on a turn
     * it considers too slow, and that turn keeps going and is written when it
     * ends. A minute is not enough for it (measured 172 seconds, and once six
     * and a half minutes).
     */
    const POLL_FAILED_MIN_MS = 300_000
    /** And never survives longer than this, whatever is still missing. */
    const POLL_ENDED_MAX_MS = 600_000

    let pollTimer: ReturnType<typeof setTimeout> | undefined
    /** Set by the first look that finds the call over: the after-call clock. */
    let callEndedAt: number | undefined
    /**
     * Whether that call ended on a failure, read once when it ended: the
     * caller can dismiss the error from the bar, and the turn it interrupted
     * would not stop running because the message went away.
     */
    let callEndedBadly = false
    let endedBackoffStep = 0
    /**
     * A look is in flight. Guards the one thing a single timer cannot: a poke
     * (a turn that ended, an answer sent from a card) landing while the
     * previous request is still open would fire a second one beside it.
     */
    let isPolling = false

    /**
     * Ends the chain. Also puts the working indicator away: whatever it was
     * waiting for is either on screen or never coming, and an indicator left
     * spinning under the conversation promises an answer nobody is producing.
     */
    function stopPolling() {
        clearTimeout(pollTimer)
        pollTimer = undefined
        callEndedAt = undefined
        callEndedBadly = false
        endedBackoffStep = 0
        isAwaitingSpokenAnswer.value = false
    }

    /** One timer for the whole chain, so two looks can never overlap. */
    function schedulePoll(delayMs: number) {
        if (isUnmounted) {
            return
        }
        clearTimeout(pollTimer)
        pollTimer = setTimeout(() => {
            void runPoll()
        }, delayMs)
    }

    async function runPoll() {
        // The look that is running will schedule the next one, with fresher
        // data than this one would have asked for.
        if (isUnmounted || isPolling) {
            return
        }
        isPolling = true
        let outcome: Awaited<ReturnType<typeof refreshMessages>>
        try {
            outcome = await refreshMessages()
        } finally {
            isPolling = false
        }
        if (isUnmounted) {
            return
        }
        // Everything spoken is on screen: the wait, if any, is over, and the
        // turns it waited for are written.
        if (outcome === 'caught-up') {
            isAwaitingSpokenAnswer.value = false
            spokenTurnsWritten()
        }
        if (voice.isActive.value) {
            callEndedAt = undefined
            callEndedBadly = false
            endedBackoffStep = 0
            schedulePoll(POLL_LIVE_MS)
            return
        }
        if (callEndedAt === undefined) {
            callEndedAt = Date.now()
            callEndedBadly = !!voice.error.value
        }
        const elapsed = Date.now() - callEndedAt
        // Only a reload that was actually applied and found nothing missing
        // ends the chain, and never before the floor: 'indeterminate' (a
        // failed request, a stale answer) keeps it alive, or a transient
        // failure would strand the last turn.
        const floorMs = callEndedBadly ? POLL_FAILED_MIN_MS : POLL_ENDED_MIN_MS
        const settled = outcome === 'caught-up' && elapsed >= floorMs
        if (settled || elapsed >= POLL_ENDED_MAX_MS) {
            stopPolling()
            // Only here: a call started again restarts the chain too, and the
            // turn the previous call left unwritten is still on its way.
            forgetSpokenTurns()
            return
        }
        const delay =
            POLL_ENDED_BACKOFF_MS[
                Math.min(endedBackoffStep, POLL_ENDED_BACKOFF_MS.length - 1)
            ]
        endedBackoffStep += 1
        schedulePoll(delay)
    }

    /** Pulls the next look closer: a turn just ended, an answer was sent. */
    const pokePoll = (delayMs = 1200) => {
        schedulePoll(delayMs)
    }

    /**
     * The page tools of the call being opened (spec `voice-page-control.md`):
     * declared with the session, and run by the handlers the provider calls,
     * both taken at the same moment so they name the same tools.
     */
    let callPageTools: ReturnType<typeof store.voicePageTools>

    const voice = useVoiceSession({
        fetchGrant: async () => {
            callPageTools = store.voicePageTools()
            const grant = await apiClient.value.createVoiceSession(
                props.agentId,
                store.localChatId,
                {
                    deviceContext: store.buildDeviceContext(),
                    externalContext: store.externalContext,
                    pageTools: callPageTools?.declaration,
                },
            )
            // The backend decides which conversation the call writes into, and
            // it may not be the one asked for: a stale id (a deleted chat, or
            // one left over from another agent) opens a new conversation rather
            // than failing the call. Follow it, or the transcript would land
            // somewhere this view is not showing.
            if (grant.chatId !== store.localChatId) {
                store.localChatId = grant.chatId
            }
            return grant
        },
        closeGrant: (sessionId) =>
            apiClient.value.closeVoiceSession(props.agentId, sessionId),
        clientTools: () => callPageTools?.clientTools,
        onTranscript: (entry) => {
            // What the AGENT says never becomes a message here, only the
            // written turn does. The transcript is text and nothing else, so
            // showing it meant a plain paragraph appearing and then being
            // swapped for the real answer with its sources and its widgets a
            // second later, and any part of it the server never writes (a
            // filler, a sentence the provider reworded) hanging around as the
            // last message until a manual reload (reported 2026-08-27). The
            // wait is shown by `isAwaitingSpokenAnswer` instead, and the
            // answer arrives once, whole.
            if (entry.role !== 'user') {
                // It is still the end of the wait: the agent has the floor.
                isAwaitingSpokenAnswer.value = false
                return
            }
            // The caller stopped talking: from here until the answer lands,
            // the turn is ours to show as working.
            isAwaitingSpokenAnswer.value = true
            spokenTurnStarted()
            appendMessage({
                // Marked as its own id space so a reload of the persisted
                // conversation replaces this rather than duplicating it, and
                // keeps it while the server does not have it yet.
                id: `${VOICE_LINE_ID_PREFIX}user-${generateId()}`,
                role: 'user',
                parts: [{ type: 'text', text: entry.text }],
            })
        },
        // The persisted answer arrives a moment after the provider finished
        // speaking it, so the reload waits for the write to land. The wait
        // indicator is NOT cleared here: a turn can end with nothing spoken
        // (the provider cut it), and the poll clearing it on caught-up is the
        // only signal that tells that apart from an answer on its way.
        onTurnEnd: () => {
            pokePoll()
        },
    })

    // A call that ends leaves no one to answer its page actions: they stop,
    // and their card goes with them.
    watch(
        () => voice.isActive.value,
        (active) => {
            if (!active) {
                store.cancelVoicePageCalls()
            }
        },
    )

    // A failure takes the stage: the error bar says what happened, and a
    // working indicator under it would promise an answer that may never come.
    watch(
        () => voice.error.value,
        (failure) => {
            if (failure) {
                isAwaitingSpokenAnswer.value = false
            }
        },
    )

    const isVoiceEnabled = computed(() => agentVoice.value?.enabled === true)

    /** What the session bar and the visualiser render. */
    const voiceState = computed(() => {
        if (voice.error.value) {
            return 'error' as const
        }
        if (voice.isConnecting.value) {
            return 'connecting' as const
        }
        if (voice.isSpeaking.value) {
            return 'speaking' as const
        }
        return voice.isActive.value ? ('listening' as const) : ('idle' as const)
    })

    /**
     * What the failure says on screen.
     *
     * Never the provider's own words: those are English prose written for
     * whoever wired the integration ("custom_llm_error: LLM Cascade Error:
     * Brain returned no response"), and passing the unrecognized ones through
     * put exactly that in front of a caller (production, 2026-08-27). The two
     * families we can tell apart get copy that says what to do about them,
     * everything else gets the honest generic one, and the raw text stays in
     * the tooltip for whoever is debugging.
     */
    const voiceErrorText = computed(() => {
        const raw = voice.error.value
        if (!raw) {
            return undefined
        }
        if (/took too long/i.test(raw)) {
            return $t('message.voiceTookTooLong')
        }
        // Their word for "the turn produced nothing I could speak", whatever
        // broke underneath: a cascade error, an empty completion, a refused
        // model. Trying again is the one thing that helps.
        if (/cascade|no response|custom_llm_error/i.test(raw)) {
            return $t('message.voiceNoAnswer')
        }
        return $t('message.voiceInterrupted')
    })

    /** Whoever is on air feeds the bars: the user, then the agent answering. */
    const voiceChannel = computed<'input' | 'output'>(() =>
        voice.isSpeaking.value ? 'output' : 'input',
    )
    const sampleVoiceLevel = () => voice.readLevel(voiceChannel.value)
    const sampleVoiceSpectrum = () => voice.readSpectrum(voiceChannel.value)

    const startVoice = async () => {
        isMuted.value = false
        await voice.start()
        if (voice.isActive.value) {
            // A fresh call restarts the clock of the chain: whatever the
            // previous one still owed, this one is live again.
            stopPolling()
            schedulePoll(POLL_LIVE_MS)
        }
    }

    const stopVoice = async () => {
        isAwaitingSpokenAnswer.value = false
        await voice.stop()
        if (isUnmounted) {
            return
        }
        // First look right away; the chain deliberately survives the call
        // (see POLL_ENDED_MIN_MS) and covers the write that lands later.
        pokePoll(0)
    }

    /** Leaves the failure behind and gives the written input back. */
    const dismissVoiceError = () => {
        voice.dismissError()
    }

    /** Same button, one step: forget the failure and open a new call. */
    const retryVoice = async () => {
        voice.dismissError()
        // The provider can report a failure on a socket that is still open,
        // and `start()` refuses while one exists: without this stop the retry
        // button only dismissed the error and never opened a new call.
        await voice.stop()
        await startVoice()
    }

    /**
     * Tool calls this client answered while the call was up, by id.
     *
     * On a call the answer does not go back to the tool call (it goes to the
     * agent as speech), so nothing in the persisted part ever changes to say it
     * was given: without this the same widget stays answerable forever and the
     * caller can send the same thing three times.
     */
    const readToolCallId = (part: unknown) =>
        (part as { toolCallId?: string })?.toolCallId ?? ''

    const answeredOnCall = ref(new Set<string>())
    const isAnsweredOnCall = (part: unknown) =>
        answeredOnCall.value.has(readToolCallId(part))

    /**
     * The verdicts of the confirms answered ON A CALL, by toolCallId. A spoken
     * verdict never resolves the tool part (the answer travels as a user
     * message), so without this the card kept its buttons and every extra tap
     * said "Confermo" again (three times in a row, measured 2026-08-07).
     * Recorded only when the send went through: `answerOnCall` leaves a failed
     * send unanswered on purpose, so the caller can tap again.
     */
    const confirmVerdictsOnCall = ref(new Map<string, boolean>())

    /**
     * Something the caller answered on screen during a call.
     *
     * In writing an answer resumes the suspended tool call, which is what makes
     * the agent continue. On a call there is no suspended call to resume: the
     * turn that drew the widget closed it on its own (a call cannot wait for a
     * screen), so the answer is said on the caller's behalf and comes back
     * spoken, in the same conversation.
     *
     * Returns true when it was said, and then the written path must NOT run:
     * `addToolOutput` would resume the turn in writing and the caller would get
     * two answers, one of them silent.
     */
    const answerOnCall = (part: unknown, spoken: string): boolean => {
        if (!voice.isActive.value) {
            return false
        }
        if (voice.sendUserMessage(spoken)) {
            answeredOnCall.value = new Set(answeredOnCall.value).add(
                readToolCallId(part),
            )
            pokePoll()
            return true
        }
        // The call is up and the answer did not get through. Saying it in
        // writing here would answer a question nobody asked in writing, so the
        // widget stays as it is and the caller can try again.
        return true
    }

    /** A form or a set of options, answered on screen. */
    const onFormAnswer = (
        tool: 'showForm' | 'showMultipleChoice',
        part: unknown,
        answer: string | { answers: { id: string; value: unknown }[] },
    ) => {
        if (answerOnCall(part, formAnswerAsSpoken(part, answer))) {
            return
        }
        addToolOutput({
            tool,
            toolCallId: readToolCallId(part),
            output: answer,
        })
    }

    /** A confirmation card, answered on screen. */
    const onConfirmRespond = (payload: {
        toolCallId: string
        confirmed: boolean
    }) => {
        const spoken = payload.confirmed
            ? $t('message.voiceConfirmed')
            : $t('message.voiceCancelled')
        if (answerOnCall({ toolCallId: payload.toolCallId }, spoken)) {
            if (answeredOnCall.value.has(payload.toolCallId)) {
                confirmVerdictsOnCall.value = new Map(
                    confirmVerdictsOnCall.value,
                ).set(payload.toolCallId, payload.confirmed)
            }
            return
        }
        addToolOutput({
            tool: 'requestConfirm',
            toolCallId: payload.toolCallId,
            output: payload.confirmed ? 'confirmed' : 'cancelled',
        })
    }

    /** The tools whose calls write a file of this chat, a version each. */
    const CHAT_FILE_TOOL_PARTS = new Set([
        'tool-editSpreadsheet',
        'tool-editWordDocument',
        'tool-createSpreadsheet',
        'tool-createWordDocument',
    ])

    /**
     * The latest version of each file the conversation shows, by its fileId:
     * the card of an earlier version offers to restore it.
     */
    const latestFileVersions = computed(() => {
        const latest = new Map<string, number>()
        for (const message of messages.value) {
            for (const part of message.parts) {
                const { type, state, output } = part as {
                    type: string
                    state?: string
                    output?: { fileId?: string; version?: number }
                }
                if (
                    CHAT_FILE_TOOL_PARTS.has(type) &&
                    state === 'output-available' &&
                    output?.fileId &&
                    output.version
                ) {
                    latest.set(
                        output.fileId,
                        Math.max(
                            latest.get(output.fileId) ?? 0,
                            output.version,
                        ),
                    )
                }
            }
        }
        return latest
    })

    /**
     * The files of the conversation by name, uploaded or written by the
     * agent, as the fileIds the agent knows them by.
     */
    const chatFileIdsByName = computed(() => {
        const byName = new Map<string, Set<string>>()
        const add = (filename?: string, fileId?: string) => {
            if (filename && fileId) {
                const key = filename.toLowerCase()
                byName.set(key, (byName.get(key) ?? new Set()).add(fileId))
            }
        }
        for (const message of messages.value) {
            for (const part of message.parts) {
                if (isFilePart(part)) {
                    add(part.filename, getFilePartMedia(part)?.storedFileName)
                    continue
                }
                const { type, state, output } = part as {
                    type: string
                    state?: string
                    output?: {
                        fileId?: string
                        filename?: string
                        version?: number
                    }
                }
                if (
                    CHAT_FILE_TOOL_PARTS.has(type) &&
                    state === 'output-available' &&
                    output?.filename
                ) {
                    add(
                        chatFileName(output.filename, output.version),
                        output.fileId,
                    )
                }
            }
        }
        return byName
    })

    /**
     * "Restore" on the card of an earlier version: asking the agent for it,
     * in a conversation that can still take a message. When two files share
     * the name, the message names the file by its fileId.
     */
    const chatFileRestore = computed(() =>
        isConversationBlocked.value
            ? undefined
            : (file: ChatFileRestoreTarget) => {
                  const twins =
                      (chatFileIdsByName.value.get(file.filename.toLowerCase())
                          ?.size ?? 0) > 1
                  input.value = twins
                      ? $t('messagePart.chatFileRestoreRequestById', file)
                      : $t('messagePart.chatFileRestoreRequest', file)
                  storeHandleSubmit()
              },
    )

    /** A suggested reply: tapping it is saying it. */
    const onSuggestedReply = (part: unknown, reply: string) => {
        if (answerOnCall(part, reply)) {
            return
        }
        input.value = reply
        storeHandleSubmit()
    }

    /** The position the caller shared from the browser. */
    const onGeolocation = (
        part: unknown,
        result: {
            latitude?: number
            longitude?: number
            displayName?: string
            error?: string
        },
    ) => {
        const spoken = result.error
            ? $t('message.voiceLocationRefused')
            : $t('message.voiceLocationShared', {
                  place:
                      result.displayName ??
                      `${result.latitude}, ${result.longitude}`,
              })
        if (answerOnCall(part, spoken)) {
            return
        }
        addToolOutput({
            tool: 'requestGeolocation',
            toolCallId: readToolCallId(part),
            output: result,
        })
    }

    /** An integration the caller connected from the card on screen. */
    const onOAuthConnected = (part: unknown, mcpServerId: string) => {
        if (answerOnCall(part, $t('message.voiceConnectionDone'))) {
            return
        }
        addToolOutput({
            tool: 'requestOAuthConnection',
            toolCallId: readToolCallId(part),
            output: { connected: true, mcpServerId },
        })
    }

    const toggleVoiceMute = () => {
        isMuted.value = !isMuted.value
        voice.setMuted(isMuted.value)
    }

    onBeforeUnmount(() => {
        isUnmounted = true
        stopPolling()
    })
    // #endregion voice

    // #region wakeups
    /** The checks the agent runs by itself (spec agent-wakeup). */
    const {
        pendingWakeup,
        isStopping: isStoppingWakeup,
        stop: stopWakeup,
        spokenTurnStarted,
        spokenTurnsWritten,
        forgetSpokenTurns,
    } = useWakeupWatch({
        agentId: () => props.agentId,
        chatId: () => store.localChatId,
        apiClient: () => apiClient.value,
        messages: () => messages.value,
        refreshMessages,
        status: () => chat.value.status,
    })
    // #endregion wakeups
</script>

<template>
    <div
        ref="chatViewEl"
        class="pk-chatbot-view-chat"
        :class="{
            'pk-chatbot-view-chat--dragover':
                isOverDropZone && agentFileUpload?.enabled,
            'pk-chatbot-view-chat--empty': showEmptyState,
            'pk-chatbot-view-chat--with-preview':
                previewBeside && !previewDocked,
        }">
        <!-- #region empty state (fullscreen) / messages — crossfade out-in -->
        <Transition name="pk-chatbot-view-chat-fade" mode="out-in">
            <PkChatbotEmptyState
                v-if="showEmptyState"
                :greeting="greeting"
                :logo="agentInterface?.logo"
                :name="store.name" />
            <PkChatbotMessages
                v-else
                class="flex flex-col flex-1 min-h-0 p-md overflow-y-auto"
                interactive
                :messages="messages"
                :confirm-verdicts-on-call="confirmVerdictsOnCall"
                :status="chat.status"
                :error="chat.error"
                :main-color="agentInterface?.mainColor"
                :text-color="agentInterface?.textColor"
                :revised-answers="revisedAnswers"
                :actions="actions"
                :message-feedbacks="messageFeedbacks"
                :feedback-message-id="feedbackDialogMessage?.id"
                :feedback-loading="isFeedbackSubmitting"
                :feedback-submitted="isFeedbackSubmitted"
                :feedback-error="feedbackSubmitError"
                :show-extended-steps="agentInterface?.showExtendedSteps"
                :hide-reasoning="agentInterface?.showReasoning === false"
                :awaiting-spoken-answer="isAwaitingSpokenAnswer"
                :host-working="hostWorking"
                :host-confirmation="hostConfirmation"
                :is-dark="isDark"
                :show-scroll-to-bottom="modifier === 'fullscreen'"
                @feedback-submit="onFeedbackSubmit($event)"
                @feedback-close="feedbackDialogMessage = undefined"
                @regenerate="regenerate"
                @auto-retry="regenerate"
                @reset-chat="startNewChat"
                @show-info="emit('show-info', $event)"
                @revise="emit('revise', $event)"
                @upvote="onUpvote"
                @downvote="onDownvote"
                @feedback="onFeedback"
                @approval-respond="addToolApprovalResponse"
                @confirm-respond="onConfirmRespond"
                @host-confirm-respond="respondHostConfirmation">
                <template #tool-showContactForm="{ part }">
                    <PkToolShowContactForm
                        :part
                        :readonly="!baseUrl"
                        :submitted="isLeadSubmitted"
                        :loading="isLoadingSubmitLead"
                        :error="submitLeadError"
                        :privacy-policy-notice="
                            agentInterface?.privacyPolicyNotice
                        "
                        @submit="onLeadSubmit" />
                </template>
                <template #tool-showSuggestedReply="{ part }">
                    <PkToolShowSuggestedReply
                        :part
                        @select="onSuggestedReply(part, $event)" />
                </template>
                <template #tool-showSources="{ part }">
                    <PkToolShowSources
                        :part
                        :on-expand-context="handleExpandSourceContext"
                        :on-download="handleDownloadSource" />
                </template>
                <template #tool-showForm="{ part }">
                    <transition mode="out-in">
                        <PkToolShowForm
                            :part
                            :answered="isAnsweredOnCall(part)"
                            @select="onFormAnswer('showForm', part, $event)" />
                    </transition>
                </template>
                <template #tool-showMultipleChoice="{ part }">
                    <transition mode="out-in">
                        <PkToolShowForm
                            :part
                            allow-custom-answer
                            :answered="isAnsweredOnCall(part)"
                            @select="
                                onFormAnswer('showMultipleChoice', part, $event)
                            " />
                    </transition>
                </template>
                <template #tool-requestOAuthConnection="{ part }">
                    <PkToolRequestOAuthConnection
                        :part
                        :backend-url="baseUrl"
                        :resolve-connection="
                            (serverName: string) =>
                                apiClient.getOAuthAuthorizeUrl(
                                    props.agentId,
                                    serverName,
                                )
                        "
                        :check-connection="
                            (serverName: string) =>
                                apiClient.getOAuthConnectionStatus(
                                    props.agentId,
                                    serverName,
                                )
                        "
                        @connected="onOAuthConnected(part, $event)" />
                </template>
                <template #tool-requestGeolocation="{ part }">
                    <PkToolRequestGeolocation
                        :part
                        :call-live="voice.isActive.value"
                        :reverse-geocode="
                            (lat: number, lon: number) =>
                                apiClient.reverseGeocode(lat, lon)
                        "
                        @result="onGeolocation(part, $event)" />
                </template>
                <template #tool-showLocation="{ part }">
                    <PkToolShowLocation
                        :part
                        :is-dark
                        :main-color="agentInterface?.mainColor"
                        :forward-geocode="
                            (query: string, lang?: string) =>
                                apiClient.forwardGeocode(query, lang)
                        " />
                </template>
                <template #tool-showDiagram="{ part }">
                    <PkToolShowDiagram :part :is-dark />
                </template>
                <template #tool-editSpreadsheet="{ part }">
                    <!-- Keyed on the state like the feed's own tool
                         components: the card mounts while the call runs, and
                         the SDK writes the output into that same part object,
                         which alone would not redraw it. -->
                    <PkToolChatFile
                        :key="getPartState(part)"
                        :part
                        :file-url="chatFileDownloadUrl"
                        :preview="openPreview"
                        :latest-versions="latestFileVersions"
                        :restore="chatFileRestore" />
                </template>
                <template #tool-editWordDocument="{ part }">
                    <PkToolChatFile
                        :key="getPartState(part)"
                        :part
                        :file-url="chatFileDownloadUrl"
                        :preview="openPreview"
                        :latest-versions="latestFileVersions"
                        :restore="chatFileRestore" />
                </template>
                <template #tool-createSpreadsheet="{ part }">
                    <PkToolChatFile
                        :key="getPartState(part)"
                        :part
                        :file-url="chatFileDownloadUrl"
                        :preview="openPreview"
                        :latest-versions="latestFileVersions"
                        :restore="chatFileRestore" />
                </template>
                <template #tool-createWordDocument="{ part }">
                    <PkToolChatFile
                        :key="getPartState(part)"
                        :part
                        :file-url="chatFileDownloadUrl"
                        :preview="openPreview"
                        :latest-versions="latestFileVersions"
                        :restore="chatFileRestore" />
                </template>
                <template #tool-shareDocument="{ part }">
                    <!-- Keyed on the state for the same reason. -->
                    <PkToolShareDocument
                        :key="getPartState(part)"
                        :part
                        :on-download="handleDownloadSource" />
                </template>
            </PkChatbotMessages>
        </Transition>
        <!-- #endregion -->

        <PkChatbotQueuedMessages
            v-if="queuedMessages.length"
            :messages="queuedMessages"
            @edit="editQueuedMessage"
            @remove="removeQueuedMessage" />

        <!-- #region input -->
        <div class="pk-chatbot-view-chat__input-stage">
            <!-- Animated accent glow radiating from the input pill -->
            <Transition name="pk-chatbot-view-chat-fade">
                <PkAuroraCanvas
                    v-if="showEmptyState"
                    class="pk-chatbot-view-chat__aurora"
                    source-selector=".pk-chatbot-input__form"
                    :main-color="agentInterface?.mainColor"
                    :tones="agentInterface?.glowTones"
                    :second-color="agentInterface?.glowSecondColor" />
            </Transition>
            <PkChatbotWakeupBar
                v-if="pendingWakeup && !isConversationBlocked"
                :pending="pendingWakeup"
                :stopping="isStoppingWakeup"
                @stop="stopWakeup" />
            <div
                v-if="isConversationBlocked"
                class="p-md border-t border-surface-3 text-center text-12 text-danger bg-surface-danger">
                {{ $t('message.chatErrorConversationBlocked') }}
            </div>
            <!-- A live call takes the place of the input: one thing on air, one
             way to hang up, and the conversation stays above it -->
            <PkVoiceSession
                v-else-if="voiceState !== 'idle'"
                :state="voiceState"
                :sample="sampleVoiceLevel"
                :spectrum="sampleVoiceSpectrum"
                :remaining-seconds="voice.remainingSeconds.value"
                :muted="isMuted"
                :error="voiceErrorText"
                :raw-error="voice.error.value"
                :live="voice.isActive.value"
                class="pk-chatbot-view-chat__voice"
                @toggle-mute="toggleVoiceMute"
                @hang-up="stopVoice"
                @retry="retryVoice"
                @dismiss="dismissVoiceError" />
            <PkChatbotInput
                v-else
                ref="inputEl"
                v-model="input"
                v-model:pending-attachments="pendingAttachments"
                :placeholder="inputMessagePlaceholder"
                :dismissable-notice="
                    dismissableNotice && chat.messages.length <= 1
                        ? dismissableNotice
                        : undefined
                "
                :page-notice="pageNotice"
                :status="inputStatus"
                :max-message-length="agentInterface?.maxMessageLength"
                :file-upload="agentFileUpload"
                :attachment-limit-reached="isAttachmentLimitReached"
                :attachment-notice="attachmentNotice"
                :voice-enabled="isVoiceEnabled"
                :queue-enabled="canQueue"
                :answer-notices="answerNotices"
                :pointer-available="pagePointerAvailable"
                :pointing="pagePointing"
                :pointer="pointerChip"
                @point="store.pagePointerStart?.()"
                @remove-pointer="pagePointer = undefined"
                @stop-generation="stopGeneration"
                @submit="storeHandleSubmit"
                @file-select="handleFileSelect"
                @dismiss-attachment-notice="clearFileLimitError"
                @dismiss-page-notice="closePageNotice"
                @open-answer-notice="openBackgroundAnswer"
                @dismiss-answer-notice="dismissBackgroundAnswer"
                @voice-start="startVoice">
                <!-- Fullscreen has no header: the agent identity lives in the
                 input pill -->
                <template v-if="modifier === 'fullscreen'" #prepend>
                    <span class="pk-chatbot-view-chat__agent-chip">
                        <PkAvatar
                            class="pk-chatbot-view-chat__agent-chip-avatar"
                            :img-src="agentInterface?.logo"
                            :name="store.name" />
                        <strong
                            v-if="store.name"
                            class="pk-chatbot-view-chat__agent-chip-name">
                            {{ store.name }}
                        </strong>
                    </span>
                </template>
            </PkChatbotInput>
        </div>
        <!-- #endregion -->
        <Transition>
            <div
                v-if="isOverDropZone && agentFileUpload?.enabled"
                class="pk-chatbot-view-chat__drop-overlay">
                <VvIcon
                    name="ri:upload-cloud-2-line"
                    class="pk-chatbot-view-chat__drop-overlay-icon" />
                <span>{{ $t('action.dropFile') }}</span>
            </div>
        </Transition>
        <!-- #region file preview: beside the chat, in the page's column when
             it gives one, or over it -->
        <Teleport
            v-if="previewBeside"
            :to="previewDockTarget"
            :disabled="!previewDocked">
            <aside
                class="pk-chatbot-view-chat__preview"
                :class="{
                    'pk-chatbot-view-chat__preview--docked': previewDocked,
                }">
                <PkFilePreview
                    v-if="previewTarget"
                    v-model:mode="previewView.mode"
                    v-model:sheet="previewView.sheet"
                    v-model:zoom="previewView.zoom"
                    v-model:fit="previewView.fit"
                    :kind="previewTarget.kind"
                    :filename="previewTarget.filename"
                    :version="previewTarget.version"
                    :versions="previewVersions"
                    :download-url="
                        chatFileDownloadUrl(previewTarget.storedFileName)
                    "
                    :preview="previewData"
                    :loading="previewLoading"
                    :error="previewError"
                    :restorable="previewRestorable"
                    resize="expand"
                    @version="showPreviewVersion"
                    @restore="restoreFromPreview"
                    @resize="previewExpanded = true"
                    @close="closePreview" />
            </aside>
        </Teleport>
        <PkFilePreviewDialog
            v-if="previewTarget && !previewBeside"
            v-model:mode="previewView.mode"
            v-model:sheet="previewView.sheet"
            v-model:zoom="previewView.zoom"
            v-model:fit="previewView.fit"
            :target="previewTarget"
            :versions="previewVersions"
            :download-url="chatFileDownloadUrl(previewTarget.storedFileName)"
            :preview="previewData"
            :loading="previewLoading"
            :error="previewError"
            :restorable="previewRestorable"
            :resize="previewInPanel ? 'shrink' : undefined"
            :fullscreen="previewFullscreen"
            @version="showPreviewVersion"
            @restore="restoreFromPreview"
            @resize="previewExpanded = false"
            @close="closePreview" />
        <!-- #endregion -->
    </div>
</template>

<style lang="scss">
    .pk-chatbot-view-chat {
        --pk-chatbot-preview-width: 520px;

        position: relative;
        display: flex;
        flex-direction: column;
        flex: 1;
        min-height: 0;

        // The preview of a Word document beside the chat: the messages and
        // the input keep a reading column, the panel takes the inline end.
        // Only from PREVIEW_PANEL_MIN_WIDTH of the script, a dialog below.
        &--with-preview {
            padding-inline-end: calc(
                var(--pk-chatbot-preview-width) + var(--spacing-12)
            );
        }

        // Where the page gives no column (the widget, the playground), one
        // frame on the ground of the chat, a little in from its edges: cut at
        // the chat's bottom, over the footer of the agent, it read as a panel
        // that stopped short.
        &__preview {
            position: absolute;
            inset-block: var(--spacing-12);
            inset-inline-end: var(--spacing-12);
            z-index: 1;
            width: var(--pk-chatbot-preview-width);
            overflow: hidden;
            border: var(--border) solid var(--color-surface-3);
            border-radius: var(--rounded-lg);
            background-color: var(--color-surface);

            // A column of the page, as its sidebar on the other side: the
            // page's height, outside the chat's header and footer. Outside
            // the chat's block, so its width is its own.
            &--docked {
                position: relative;
                inset: auto;
                flex-shrink: 0;
                width: clamp(520px, 38vw, 760px);
                height: 100%;
                border-width: 0 0 0 var(--border);
                border-radius: 0;
            }
        }

        // Empty state (fullscreen): center the greeting + input block
        // vertically in the available space
        &--empty {
            justify-content: center;
            gap: var(--spacing-md);

            // In a phone-sized chat the input docks to the bottom edge
            // instead (Gemini-like), with the greeting centered in the space
            // above. Keyed on the chat's own width (`--phone`, measured in
            // PkChatbot), not the viewport: a narrow preview frame on a
            // desktop screen docks the same way
            .pk-chatbot--phone & {
                justify-content: flex-start;

                .pk-chatbot-empty-state {
                    flex: 1;
                    justify-content: center;
                }
            }
        }

        // Anchors the glow to the input pill
        &__input-stage {
            position: relative;
        }

        // Radiates outwards from the pill, biased upwards (the pill itself
        // paints above, being later in the DOM)
        &__input-stage &__aurora {
            inset: calc(-1 * var(--spacing-224)) calc(-1 * var(--spacing-96))
                calc(-1 * var(--spacing-160));

            // With the input docked at the bottom the glow becomes a wide
            // dome filling the lower part of the chat (Gemini-like); same
            // threshold as the docking above
            .pk-chatbot--phone & {
                --aurora-spread: 1.6;

                // Tall enough that the glow tail never meets the canvas
                // edge fade (it would draw a visible horizontal band)
                inset: calc(-1 * var(--spacing-384) - var(--spacing-128))
                    calc(-1 * var(--spacing-96)) calc(-1 * var(--spacing-160));
            }
        }

        // Agent identity inside the input pill (fullscreen has no header).
        // A rounded box rather than a pill: a pill around a tile drew two
        // curves that did not run together.
        &__agent-chip {
            display: inline-flex;
            align-items: center;
            gap: var(--spacing-6);
            flex-shrink: 0;
            padding: var(--spacing-4) var(--spacing-10) var(--spacing-4)
                var(--spacing-4);
            border: 1px solid var(--color-surface-3);
            border-radius: var(--rounded-lg);
            background-color: var(--color-surface);

            // Don't crowd the textarea in a narrow chat. Keyed on the chat's
            // own width (`--tight`, measured in PkChatbot), not the viewport:
            // a preview frame squeezed by side panels on a desktop screen is
            // as narrow as a phone page. Below that threshold the chip would
            // leave the textarea a couple of hundred pixels at best, and in a
            // phone-sized frame a few characters, wrapping the placeholder
            // into a tall pill. The empty state shows the avatar at the same
            // threshold: one of the two carries the agent identity.
            .pk-chatbot--tight & {
                display: none;
            }
        }

        &__agent-chip-avatar {
            width: var(--spacing-24);
            height: var(--spacing-24);
            font-size: var(--text-10);
        }

        &__agent-chip-name {
            font-size: var(--text-12);
            font-weight: var(--font-semibold);
            color: var(--color-word-2);
            white-space: nowrap;
        }

        // Crossfade empty state ↔ messages: exit faster than enter so the
        // switch never feels like a snap
        &-fade-enter-active {
            transition: opacity 250ms var(--ease-out);
        }

        &-fade-leave-active {
            transition: opacity 150ms var(--ease-out);
        }

        &-fade-enter-from,
        &-fade-leave-to {
            opacity: 0;
        }

        @media (prefers-reduced-motion: reduce) {
            &-fade-enter-active,
            &-fade-leave-active {
                transition: none;
            }
        }

        &__drop-overlay {
            position: absolute;
            inset: var(--spacing-sm) var(--spacing-sm) var(--spacing-sm)
                var(--spacing-sm);
            z-index: 1;
            display: flex;
            flex-direction: column;
            align-items: center;
            justify-content: center;
            gap: var(--spacing-xs);
            background-color: color-mix(
                in srgb,
                var(--color-surface) 85%,
                transparent
            );
            border-radius: var(--rounded-xl);
            border: var(--spacing-2) dashed var(--color-surface-5);
            pointer-events: none;
            color: var(--color-word-3);

            &-icon {
                font-size: var(--spacing-32);
            }
        }
    }
</style>
